import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { ApiError, callFn, isAuthed, loginPassword, logout as apiLogout, onAuthLost } from '../lib/api';
import type { Counts, Me } from '../lib/types';

type Status = 'loading' | 'out' | 'in' | 'passkey';
export interface Toast { id: number; kind: 'ok' | 'err' | 'info'; text: string }

interface AuthState {
  status: Status;
  me: Me | null;
  counts: Counts;
  /** Why the last bootstrap failed (shown on the sign-in page when the session is gone). */
  bootError: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
  /** Called by the passkey step after Nexus accepted the assertion. */
  passkeyDone: () => Promise<void>;
  refreshMe: () => Promise<void>;
  toast: (text: string, kind?: Toast['kind']) => void;
  toasts: Toast[];
}

const EMPTY: Counts = { waiting: 0, now: 0, openInitiatives: 0, decisions: 0 };
const Ctx = createContext<AuthState | null>(null);
export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be used within AuthProvider');
  return v;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>(isAuthed() ? 'loading' : 'out');
  const [me, setMe] = useState<Me | null>(null);
  const [bootError, setBootError] = useState<string | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const id = useRef(1);

  const toast = useCallback((text: string, kind: Toast['kind'] = 'info') => {
    const n = id.current++;
    setToasts((t) => [...t, { id: n, kind, text }]);
    window.setTimeout(() => setToasts((t) => t.filter((x) => x.id !== n)), 4000);
  }, []);

  const refreshMe = useCallback(async () => {
    if (!isAuthed()) { setMe(null); setStatus('out'); return; }
    try {
      const m = await callFn<Me>('me');
      setMe(m); setStatus('in'); setBootError(null);
    } catch (e) {
      if (!isAuthed()) { setMe(null); setStatus('out'); return; }   // callFn cleared the session
      // NEXUS-757: Helm requires a passkey step-up for password sessions; keep the tokens and ask for it.
      if (e instanceof ApiError && e.code === 'PASSKEY_REQUIRED') { setMe(null); setBootError(null); setStatus('passkey'); return; }
      // Signed in but not usable (not a member yet, server down): say so instead of looping on a spinner.
      setBootError(e instanceof Error ? e.message : String(e));
      setStatus((s) => (s === 'loading' ? 'out' : s));
    }
  }, []);

  useEffect(() => { void refreshMe(); }, [refreshMe]);
  useEffect(() => onAuthLost(() => { setMe(null); setStatus('out'); }), []);
  useEffect(() => {
    if (status !== 'in') return;
    const t = window.setInterval(() => { if (!document.hidden) void refreshMe(); }, 60000);
    const vis = () => { if (!document.hidden) void refreshMe(); };
    document.addEventListener('visibilitychange', vis);
    return () => { window.clearInterval(t); document.removeEventListener('visibilitychange', vis); };
  }, [status, refreshMe]);

  const signIn = useCallback(async (email: string, password: string) => {
    setBootError(null);
    await loginPassword(email.trim(), password);
    setStatus('loading');
    await refreshMe();
  }, [refreshMe]);

  const passkeyDone = useCallback(async () => { setStatus('loading'); await refreshMe(); }, [refreshMe]);

  const signOut = useCallback(() => { apiLogout(); setMe(null); setStatus('out'); setBootError(null); }, []);

  return (
    <Ctx.Provider value={{ status, me, counts: me?.counts ?? EMPTY, bootError, signIn, signOut, passkeyDone, refreshMe, toast, toasts }}>
      {children}
    </Ctx.Provider>
  );
}
